import { Box, Grid, Link, Stack, styled, Typography } from '@mui/material';
import React, { DragEvent, useMemo } from 'react';
import { StyledParams } from 'types/common';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputText } from 'app/components/elements/InputText';
import { useInjection } from './hooks';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { Controller } from 'react-hook-form';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker } from '../../components/elements/DatePicker';
import dayjs from 'dayjs';
import { InputSelect } from '../../components/elements/InputSelect';
import { Icon_Arrow } from 'assets';
import { lowerCase, startCase } from 'lodash';
import { getNameOption } from 'utils/convertData';
import { useRoomContext } from 'contexts/RoomContext';
import { dropdownValueTypes } from 'const/option';
import { entriesInclude } from 'utils/entries';

export const RoomReportCreateIssuePage = () => {
  const {
    handleDeleteImg,
    handleSubmit,
    register,
    control,
    setValue,
    errors,
    watch,
    fileInputRef,
    isDragging,
    setIsDragging,
    photos,
    paths
  } = useInjection();
  const { systemDropdownValues, singleEntryField, users } = useRoomContext();
  const reportIssueDropdownOption = useMemo(() => {
    const options = systemDropdownValues?.filter((item) =>
      item.options.some((option) => option.type === dropdownValueTypes.reportIssue)
    );
    return options;
  }, [systemDropdownValues]);

  const roomLevelIssueOptionSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.reportIssue)
    );
  }, [singleEntryField]);
  return (
    <WrapperCreatePage>
      <Stack
        width={'100%'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <TitlePageStyled>Report</TitlePageStyled>
        <ButtonCustom
          sx={{ width: 'max-content', height: '46px', padding: '0 20px' }}
          label={'Back'}
          onClick={() => {
            window.history.go(-1);
          }}
        />
      </Stack>
      <Stack mt={0.5} gap={2.5} direction="row">
        {paths.map((path, index) => (
          <Stack
            key={index}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={2}
            mr={0.5}>
            <TextPath fontWeight={paths.length === index + 1 ? '900' : '500'}>{path}</TextPath>
            {index < paths.length - 1 && <Icon_Arrow color={'#226AAA'} />}
          </Stack>
        ))}
      </Stack>
      <Stack
        style={{
          borderRadius: '10px',
          border: '1px solid rgba(144, 144, 144, 0.20)',
          padding: '14px',
          maxWidth: 1200
        }}>
        <>
          <Stack flexDirection={'row'} gap={3} justifyContent={'space-between'}>
            <Grid
              style={{ width: '50%', height: '0' }}
              container
              spacing={4.5}
              display={'grid'}
              gridTemplateColumns={'repeat(3, 1fr)'}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Stack gap={1.5}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, marginBottom: '4px' }}>
                    Add New Photos
                  </Typography>
                  <input
                    accept={'image/*'}
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    multiple
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        setValue('uploadedFiles', [...(photos || []), ...Array.from(files)]);
                      }
                    }}
                  />
                  <Box
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e: DragEvent<HTMLDivElement>) => {
                      e.preventDefault();
                      if (!isDragging) setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e: DragEvent<HTMLDivElement>) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const files = e.dataTransfer.files;
                      if (files && files.length > 0) {
                        setValue('uploadedFiles', [...(photos || []), ...Array.from(files)]);
                      }
                    }}
                    sx={{
                      width: '100%',
                      height: 120,
                      border: '1px dashed #ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      flexDirection: 'column',
                      bgcolor: isDragging ? '#e3f2fd' : '#f5f5f5',
                      transition: 'background-color 0.2s'
                    }}>
                    <UploadFileIcon sx={{ color: isDragging ? '#1976d2' : '#888' }} />
                    <span
                      style={{
                        textAlign: 'center',
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: '12px'
                      }}>
                      Click or drag photos here
                    </span>
                  </Box>
                  {errors.uploadedFiles?.message && (
                    <span style={{ color: 'red', fontSize: '16px' }}>
                      {errors.uploadedFiles?.message}
                    </span>
                  )}
                </Stack>
              </Grid>

              {photos?.map((item, index) => {
                return (
                  <Grid item xs={12} sm={12} md={12} lg={12} key={index}>
                    <Stack gap={1.5}>
                      <TextEllipsis
                        text={item?.name}
                        width={'95%'}
                        line={1}
                        styles={{ padding: '0 8px' }}
                      />
                      <Stack
                        width={'100%'}
                        style={{
                          position: 'relative',
                          border: '1px solid #D9D9D9',
                          borderRadius: '10px'
                        }}>
                        <img
                          height={120}
                          alt={'icon-image-room'}
                          src={URL.createObjectURL(item)}
                          style={{ width: '100%', objectFit: 'contain' }}
                        />
                        <img
                          width={'16px'}
                          height={'16px'}
                          alt={'icon-room'}
                          onClick={() => handleDeleteImg(index)}
                          src={'/icons/icon_delete.svg'}
                          style={{
                            position: 'absolute',
                            top: '10px',
                            right: '13px',
                            cursor: 'pointer'
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
            <Stack gap={2} flex={1} alignItems={'flex-start'} justifyContent={'flex-start'} px={4}>
              <InputText
                title={'Equipment Id'}
                inputError={errors.equipmentId?.message}
                placeholder={'Enter Equipment Id'}
                inputProps={{
                  ...register('equipmentId')
                }}
              />
              <InputText
                title={'Name'}
                inputError={errors.name?.message}
                placeholder={'Enter Name'}
                inputProps={{
                  ...register('name')
                }}
              />
              <InputText
                title={'Description'}
                inputError={errors.description?.message}
                placeholder={'Enter Description'}
                inputProps={{
                  ...register('description')
                }}
              />
              {reportIssueDropdownOption?.map((opt) => (
                <React.Fragment key={opt.id}>
                  <Controller
                    control={control}
                    name={`option_${opt.id}`}
                    render={({ field: { onChange, value }, fieldState }) => (
                      <InputSelect
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                        inputError={fieldState.error?.message}
                        value={value}
                        placeholder="Select Issue Type"
                        onChange={(val) => {
                          onChange(val);
                        }}
                        options={opt?.dropdownValues?.map((item) => ({
                          label: item.value,
                          value: item.value
                        }))}
                      />
                    )}
                  />

                  {watch(`option_${opt.id}`) === 'OTHER' && (
                    <Controller
                      name={`option_${opt.id}_name`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <InputText
                          title="Other Issue"
                          placeholder="Enter Other Issue"
                          inputError={fieldState.error?.message}
                          inputProps={{ ...field }}
                        />
                      )}
                    />
                  )}
                  {watch(`option_${opt.id}`) === 'Action By' && (
                    <>
                      <Controller
                        name={`actionBy`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputSelect
                            title="Action By:"
                            placeholder="Select User"
                            value={field.value}
                            inputError={fieldState.error?.message}
                            onChange={field.onChange}
                            options={[
                              ...(users?.map((item) => ({
                                label: item.label,
                                value: item.label
                              })) ?? []),
                              { label: 'Other', value: 'OTHER' }
                            ]}
                          />
                        )}
                      />
                      <Stack gap={'10px'}>
                        <Controller
                          control={control}
                          render={({ field: { value }, fieldState }) => (
                            <DatePicker
                              inputError={fieldState?.error?.message || ''}
                              style={{ flexDirection: 'column' }}
                              title={'Choose Action By Date'}
                              onChange={(val) =>
                                setValue('actionByDate', val ? dayjs(val).toISOString() : '')
                              }
                              value={value ? dayjs(value) : undefined}
                            />
                          )}
                          name={'actionByDate'}
                        />
                      </Stack>
                    </>
                  )}
                </React.Fragment>
              ))}
              {roomLevelIssueOptionSingleField?.map((field) => (
                <Stack style={{ width: '100%' }} key={field.id}>
                  {field.type === 'DATE' ? (
                    <Controller
                      name={`single_${field.id}`}
                      control={control}
                      render={({ field: f }) => (
                        <DatePicker
                          title={`${field.name}:`}
                          onChange={(val) =>
                            setValue(
                              `single_${field.id}`,
                              val ? dayjs(val).format('YYYY-MM-DD') : ''
                            )
                          }
                          value={f.value ? dayjs(f?.value as string) : undefined}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name={`single_${field.id}`}
                      control={control}
                      render={({ field: f, fieldState }) => (
                        <InputText
                          title={`${field.name}:`}
                          placeholder={`Enter ${field.name}`}
                          inputProps={{ ...f }}
                          inputError={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                </Stack>
              ))}
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSelect
                    onChange={onChange}
                    inputError={errors.actionBy?.message || ''}
                    title={'Action By'}
                    value={value}
                    placeholder={'Select a user'}
                    options={[
                      ...(users?.map((item) => ({
                        label: item.label,
                        value: item.label
                      })) ?? []),
                      { label: 'Other', value: 'OTHER' }
                    ]}
                  />
                )}
                name={`actionBy`}
              />

              {watch('actionBy') === 'OTHER' && (
                <InputText
                  title={'Action By Name'}
                  inputError={errors.actionByName?.message}
                  placeholder={'Enter Action By Name'}
                  inputProps={{
                    ...register('actionByName')
                  }}
                />
              )}

              <Stack style={{ width: '100%' }}>
                <Controller
                  control={control}
                  render={({ field: { value } }) => (
                    <DatePicker
                      inputError={errors?.actionByDate?.message}
                      style={{ flexDirection: 'column' }}
                      title={'Choose Action By Date'}
                      onChange={(val) => {
                        const date = dayjs(val);
                        if (!val || !date.isValid()) {
                          setValue('actionByDate', '');
                        } else {
                          setValue('actionByDate', date.toISOString());
                        }
                      }}
                      value={value ? dayjs(value) : undefined}
                    />
                  )}
                  name={'actionByDate'}
                />
              </Stack>
              <InputText
                title={'Issue Notes'}
                inputError={errors.note?.message}
                placeholder={'Enter Issue Notes'}
                inputProps={{
                  ...register('note')
                }}
              />
              <ButtonCustom style={{ width: 166 }} onClick={handleSubmit} label={'Save'} />
            </Stack>
          </Stack>
        </>
      </Stack>
    </WrapperCreatePage>
  );
};

const WrapperCreatePage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: '20px'
}));
const TitlePageStyled = styled(Typography)(({ theme }: StyledParams) => ({
  margin: '20px 0',
  color: theme.colors.base,
  fontSize: '24px',
  fontWeight: 900,
  fontFamily: `'Avenir',sans-serif`
}));
const TextPath = styled(Link)(() => ({
  fontSize: '16px',
  color: '#226AAA',
  fontFamily: `'Avenir',sans-serif`,
  textDecoration: 'none'
}));
